import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Download, Search, ChevronRight } from 'lucide-react';
import type { TicketDto, TicketStatus } from '@bstm/shared';
import { api, ApiError, getToken } from '../api/client';
import { toast } from 'sonner';

const STATUSES: TicketStatus[] = [
  'NEEDS_REVIEW',
  'READY_FOR_SUBMISSION',
  'SUBMITTED',
  'FILLED',
  'CANCELLED',
  'DRAFT',
];

function statusClass(s: TicketStatus): string {
  return {
    DRAFT: 'bg-zinc-700 text-zinc-300',
    NEEDS_REVIEW: 'bg-amber-600/30 text-amber-300',
    READY_FOR_SUBMISSION: 'bg-indigo-600/30 text-indigo-300',
    SUBMITTED: 'bg-blue-600/30 text-blue-300',
    FILLED: 'bg-emerald-600/30 text-emerald-300',
    CANCELLED: 'bg-red-600/30 text-red-300',
  }[s];
}

function fmtCreator(t: TicketDto): string {
  if (t.creatorName) return t.creatorName;
  if (t.creatorPhone) return `…${t.creatorPhone.slice(-4)}`;
  return '—';
}

function dashboardTransitions(current: TicketStatus): TicketStatus[] {
  switch (current) {
    case 'READY_FOR_SUBMISSION':
      return ['SUBMITTED'];
    case 'DRAFT':
      return ['SUBMITTED', 'CANCELLED'];
    case 'SUBMITTED':
      return ['FILLED', 'CANCELLED'];
    case 'NEEDS_REVIEW':
    case 'FILLED':
    case 'CANCELLED':
      return [];
  }
}

function StatusSelect({ ticket }: { ticket: TicketDto }) {
  const qc = useQueryClient();
  const patch = useMutation({
    mutationFn: (status: TicketStatus) =>
      api(`/tickets/${ticket.id}`, { method: 'PATCH', json: { status } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket', ticket.id] });
      toast.success('Status updated');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });
  const allowed = dashboardTransitions(ticket.status);
  if (allowed.length === 0) {
    return (
      <span
        className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${statusClass(ticket.status)}`}
      >
        {ticket.status.replace(/_/g, ' ')}
      </span>
    );
  }
  return (
    <select
      value={ticket.status}
      onChange={(e) => {
        const next = e.target.value as TicketStatus;
        if (next !== ticket.status) patch.mutate(next);
      }}
      onClick={(e) => e.stopPropagation()}
      disabled={patch.isPending}
      className={`text-xs px-2 py-0.5 rounded border-0 cursor-pointer appearance-none ${statusClass(
        ticket.status,
      )}`}
    >
      <option value={ticket.status} className="bg-zinc-900 text-zinc-100">
        {ticket.status.replace(/_/g, ' ')}
      </option>
      {allowed.map((s) => (
        <option key={s} value={s} className="bg-zinc-900 text-zinc-100">
          {s.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

interface SummaryCardProps {
  label: string;
  value: number | string;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}

function SummaryCard({ label, value, tone = 'default' }: SummaryCardProps) {
  const toneClass = {
    default: 'text-zinc-100',
    good: 'text-emerald-400',
    warn: 'text-amber-400',
    bad: 'text-red-400',
  }[tone];
  return (
    <div className="card p-4">
      <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-mono mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [tickerSearch, setTickerSearch] = useState('');
  const [mineOnly, setMineOnly] = useState(false);

  const params = new URLSearchParams();
  if (statusFilter) params.set('status', statusFilter);
  if (tickerSearch.trim()) params.set('tickerLike', tickerSearch.trim().toUpperCase());
  if (mineOnly) params.set('mine', 'true');

  const ticketsQ = useQuery<{ tickets: TicketDto[] }>({
    queryKey: ['tickets', statusFilter, tickerSearch, mineOnly],
    queryFn: () => api(`/tickets?${params.toString()}`),
  });

  const allQ = useQuery<{ tickets: TicketDto[] }>({
    queryKey: ['tickets', 'summary'],
    queryFn: () => api('/tickets'),
    refetchInterval: 30_000,
  });

  const tickets = ticketsQ.data?.tickets ?? [];
  const all = allQ.data?.tickets ?? [];

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const todayCutoff = Math.floor(startOfDay.getTime() / 1000);

  const filledToday = all.filter((t) => t.status === 'FILLED' && (t.filledAt ?? 0) >= todayCutoff);
  const awaitingReview = all.filter((t) => t.status === 'NEEDS_REVIEW').length;
  const pending = all.filter(
    (t) => t.status === 'NEEDS_REVIEW' || t.status === 'READY_FOR_SUBMISSION' || t.status === 'SUBMITTED',
  ).length;

  function downloadCsv() {
    const token = getToken();
    if (!token) return;
    const url = `/api/tickets/export.csv?${params.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `tickets-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(objUrl);
      });
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-semibold">Tickets</h1>
        <div className="flex gap-2">
          <button onClick={downloadCsv} className="btn-secondary text-sm hidden sm:inline-flex">
            <Download size={16} /> Export CSV
          </button>
          <Link
            to="/tickets/new"
            data-tour="new-ticket-btn"
            className="btn-primary text-sm flex-1 sm:flex-initial justify-center"
          >
            <Plus size={16} /> New ticket
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <SummaryCard label="Filled today" value={filledToday.length} tone="good" />
        <SummaryCard
          label="Awaiting review"
          value={awaitingReview}
          tone={awaitingReview > 0 ? 'warn' : 'default'}
        />
        <SummaryCard label="In flight" value={pending} tone={pending > 0 ? 'warn' : 'default'} />
        <SummaryCard label="Total" value={all.length} />
      </div>

      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
        <div className="relative flex-1 min-w-[140px] sm:flex-initial">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={tickerSearch}
            onChange={(e) => setTickerSearch(e.target.value)}
            placeholder="Search ticker…"
            className="pl-9 font-mono uppercase w-full sm:w-48"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as TicketStatus | '')}
          className="text-sm"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-400">
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
          />
          Mine
        </label>
        <span className="text-xs text-zinc-500">{tickets.length} shown</span>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-zinc-400 text-left">
            <tr>
              <th className="px-3 md:px-4 py-2">Ticker</th>
              <th className="px-3 md:px-4 py-2">Side</th>
              <th className="px-3 md:px-4 py-2 text-right">Shares</th>
              <th className="hidden lg:table-cell px-4 py-2">Reason</th>
              <th className="hidden md:table-cell px-4 py-2">By</th>
              <th className="px-3 md:px-4 py-2">Status</th>
              <th className="hidden md:table-cell px-4 py-2">Created</th>
              <th className="px-2 py-2 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr
                key={t.id}
                onClick={() => navigate(`/tickets/${t.id}`)}
                className="border-t border-zinc-800 hover:bg-zinc-900/50 cursor-pointer"
              >
                <td className="px-3 md:px-4 py-2 font-mono">{t.ticker}</td>
                <td className={`px-3 md:px-4 py-2 font-medium ${t.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {t.side}
                </td>
                <td className="px-3 md:px-4 py-2 text-right font-mono">{t.shares.toLocaleString()}</td>
                <td className="hidden lg:table-cell px-4 py-2 text-zinc-400 truncate max-w-md">{t.reason}</td>
                <td className="hidden md:table-cell px-4 py-2 text-zinc-400 text-xs font-mono">{fmtCreator(t)}</td>
                <td className="px-3 md:px-4 py-2">
                  <StatusSelect ticket={t} />
                </td>
                <td className="hidden md:table-cell px-4 py-2 text-zinc-500 text-xs whitespace-nowrap">
                  {new Date(t.createdAt * 1000).toLocaleString()}
                </td>
                <td className="px-2 py-2 text-zinc-600">
                  <ChevronRight size={16} />
                </td>
              </tr>
            ))}
            {tickets.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-zinc-500 py-8">
                  {tickerSearch || statusFilter || mineOnly ? 'No tickets match filters.' : 'No tickets yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
